<template>
  <div
    class="greedy"
    :class="{'fake-indent': setFakeIndent, slim: slim}">
    <vue-editor
      id="vue-editor-wrapper"
      ref="vueEditor"
      v-model="newOutboundMessage"
      :class="[customClass]"
      :data-gramm="!slim"
      :data-gramm_editor="!slim"
      :data-enable-grammarly="!slim"
      :editor-options="editorOptions"
      :placeholder="placeholder || 'Type a message...'"
      :disabled="disabled"
      @blur="lostFocus"
      @focus="gotFocus"
      @selection-change="onSelectionChange"
      @text-change="onTextChange"
      @click.native="preventPropagation"
      @keydown.exact.46.native="deleteDirection = -1"
      @keydown.exact.8.native="deleteDirection = 1"
      @keydown.exact.13.native="keypressHandler" />
    <!-- Invisible element. Used to load the oldDeltas when editor above changes contant. -->
    <div id="quill-worker"/>
  </div>

  <!-- TODO Grammarly issues -->
  <!-- https://medium.com/beyond-webspellchecker/befriending-wysiwyg-editors-text-highlighting-with-virtual-underlines-48c80a680b2f -->
  <!-- Solution that didn't worked for us but try to use it with pure quill instance and not vue-editor2 - https://github.com/quilljs/quill/issues/574 -->
</template>

<script>
import { VueEditor, Quill } from 'vue2-editor';

import randomString from '@/helpers/randomString';
import retrieveImageFromClipboardAsBlob from '@/helpers/imageUtils';
import {
  whatsappFormatMessage,
  removeExtraSpaces,
  whatsAppFormatToHTML,
  convertTagToUnderlineVersion,
  convertUnderscoreToSpace,
} from '@/helpers/stringHelpers';

export default {
  name: 'MessageInputTextarea',
  components: {
    VueEditor,
  },
  props: {
    customClass: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    preventSubmit: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      type: String,
      required: false,
      default: '',
    },
    maxlength: {
      type: Number,
      required: false,
      default: null,
    },
    allowEnterNewline: {
      type: Boolean,
      required: false,
      default: false,
    },
    recalculateTagNumbers: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Slim type should have buttons in line with input. Use it only when you have one or two buttons. */
    slim: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      editorOptions: {
        modules: {
          toolbar: false,
        },
      },
      newOutboundMessage: '',
      selection: {
        index: 0,
      },
      skipCycle: false,
      tagPlaceholders: 0,
      oldText: '',
      pauseChangeListener: false,
      unpauseTimeout: setTimeout(() => {}, 0),
      oldDeltasQuill: null,
      // If tag is a first delta and user wants to set cursor in front of it then one space will be added in front
      // of the tag so we have to fake the indent to compensate for the space so visually nothing changes for the user.
      // User must not know there is a space in front of a first tag.
      setFakeIndent: false,
      // Pauses the check if there is the need to remove the space in front of the tag in case there is a long tag name and we need a loop to get to it's beginning.
      pauseLoop: false,
      deleteDirection: -1,
      lastPosition: {},
      pasted: false,
    };
  },
  watch: {
    value() {
      this.newOutboundMessage = this.value;
    },
  },
  mounted() {
    // We need to override the default enter key binding to prevent newline and disable it or send new message depending on the defined configuration.
    if (!this.allowEnterNewline) {
      this.$refs.vueEditor.quill.keyboard.bindings[13].unshift({
        key: 13,
        handler: this.keypressHandler,
      });
    }

    this.$refs.vueEditor.quill.keyboard.bindings[9].unshift({
      key: 9,
      handler: () => {
        // This is the basic version just to make it work and it needs additional work every time it is used. For a rewrite try to make this generic to always find the next input item and focuses it automatically.
        this.$emit('tab');
      },
    });

    this.$refs.vueEditor.$el.addEventListener('paste', this.checkIfMediaPasted);
    this.oldDeltasQuill = new Quill('#quill-worker');

    if (this.slim) {
      this.$refs.vueEditor.quill.keyboard.bindings[13].unshift({
        key: 13,
        handler: this.keypressHandler,
      });
    }
  },
  beforeDestroy() {
    this.$refs.vueEditor.$el.removeEventListener('paste', this.checkIfMediaPasted);
  },
  methods: {
    retrieveImageFromClipboardAsBlob,
    randomString,
    /**
     * Adds a content to the last known selection index and then focuses message input field with caret after the newly added content.
     *
     * @param content String to be added to the message.
     */
    addAtLastCaretPosition(content) {
      this.$refs.vueEditor.quill.insertText(this.selection.index, content);
      this.$refs.vueEditor.quill.setSelection(this.selection.index + content.length, 0);
      this.$emit('textInserted'); // needed for the parent to scroll it back into place
      this.$refs.vueEditor.quill.focus();
    },
    /** converts tag like bussines_name into "bussines name" for better readability. */
    convertTagToHumanReadable(tag) {
      return tag.replaceAll(/_/g, ' ');
    },
    addTag(text = ' ') {
      let rectSizeStart;
      let startWidth;
      if (this.slim) {
        // Get width before tag is added so the width change can be calculated to position the scroll in the element correctly.
        rectSizeStart = this.$refs.vueEditor.$el.childNodes[1].childNodes[0].childNodes[0].getBoundingClientRect();
        startWidth = rectSizeStart.right - rectSizeStart.left;
      }

      this.ignoreSelectionChange = true;
      const initialIndex = this.selection.index;
      this.$refs.vueEditor.quill.insertText(initialIndex, this.convertTagToHumanReadable(text), 'code', true);
      this.$refs.vueEditor.quill.insertText(initialIndex + text.toString().length, ' ', 'code', false);
      this.$emit('textInserted');
      if (text === ' ') {
        this.setTagNumbers();
      }
      setTimeout(() => {
        this.$refs.vueEditor.quill.focus();
        // We cant just set selection based on the inserted number length because if there is already 10 tags and user decides to insert one more at the beginning then our cursor will be positioned 1 position too far.
        // That is why we need to get the location where the tag is inserted and see how many characters a tag has and set selection based on that.
        const content = this.$refs.vueEditor.quill.getContents(initialIndex);
        if (
          content &&
          content.ops[0] &&
          content.ops[0].attributes &&
          content.ops[0].attributes.code
        ) {
          this.selection = { index: initialIndex + content.ops[0].insert.toString().length + 1, length: 0 };
          this.$refs.vueEditor.quill.setSelection(this.selection.index, 0);
        }

        if (this.slim) {
          // reset scroll position
          const rectSizeEnd = this.$refs.vueEditor.$el.childNodes[1].childNodes[0].childNodes[0].getBoundingClientRect();
          const endWidth = rectSizeEnd.right - rectSizeEnd.left;
          this.$refs.vueEditor.$el.childNodes[1].childNodes[0].scrollLeft += rectSizeEnd.x - rectSizeStart.x + (endWidth - startWidth);
        }
      }, 0);
      this.ignoreSelectionChange = false;
    },
    setTagNumbers() {
      // Don't do it if not in tag counter mode
      if (!this.recalculateTagNumbers) {
        return;
      }
      const selection = this.$refs.vueEditor.quill.getSelection();
      const currentIndex = selection ? selection.index : undefined;
      const items = this.$refs.vueEditor.quill.getContents();
      let count = 0;
      items.ops.forEach((item) => {
        if (item.attributes && item.attributes.code === true) {
          count++;
          item.insert = count.toString();
        }
      });
      this.tagPlaceholders = count;
      this.$emit('tag-count', this.tagPlaceholders);
      this.$refs.vueEditor.quill.setContents(items.ops);
      if (currentIndex) {
        this.$refs.vueEditor.quill.setSelection(currentIndex, 0);
      }
    },
    /** Ensures there are no duplicate tag numbers present. */
    reCountTagNumbers() {
      if (!this.recalculateTagNumbers) {
        this.unpauseChangeListener();
        return;
      }
      this.ignoreSelectionChange = true;
      setTimeout(() => {
        let workingText = whatsAppFormatToHTML(this.newOutboundMessage.trim(), false);
        this.tagPlaceholdersCount = (workingText.match(/<code>\d+<\/code>/g) || []).length;
        for (let i = 0; i < this.tagPlaceholdersCount; i++) {
          workingText = workingText.replace(/<code>\d+<\/code>/, `{*{${i + 1}}*}`);
        }
        workingText = workingText.replaceAll(/{\*{(\d+)}\*}/g, '<code>$1</code>');
        this.setHtml(workingText);
        setTimeout(() => {
          this.ignoreSelectionChange = false;
        }, 30);
        this.unpauseChangeListener();
      }, 100);
    },
    /**
     * (Un)Bolds the user selection.
     * It will first check if this selection is already bolded and bold it if it is not. If it is then it will unbold it.
     */
    boldIt() {
      const isBoldedAlready = this.$refs.vueEditor.quill.getFormat(this.selection.index, this.selection.length).bold;
      this.$refs.vueEditor.quill.formatText(this.selection.index, this.selection.length, 'bold', !isBoldedAlready);
      this.$refs.vueEditor.quill.focus();
      this.$refs.vueEditor.quill.format('bold', !isBoldedAlready);
      this.checkFormatting();
    },
    checkFormatting() {
      this.$emit('formatting', this.$refs.vueEditor.quill.getFormat(this.selection.index, this.selection.length));
    },
    /**
     * Checks the paste event for images and emits them for media modal.
     *
     * @param clipboardEvent
     */
    checkIfMediaPasted(clipboardEvent) {
      const imagesFromClipboard = this.retrieveImageFromClipboardAsBlob(clipboardEvent);
      if (imagesFromClipboard) {
        // If images are found in the paste event then prevent default paste action.
        clipboardEvent.preventDefault();
        const clipboardData = clipboardEvent.clipboardData || window.clipboardData;
        imagesFromClipboard.forEach((image) => {
          const imageObjectForMediaModal = {
            id: this.randomString(),
            file: image,
            name: 'pasted-image.png',
            caption: clipboardData.getData('Text'),
          };
          this.$nextTick(() => {
            this.$eventBus.$emit('mediaDrop', imageObjectForMediaModal);
          });
        });
      } else {
        // if not media then remove all formatting
        setTimeout(() => {
          this.pasted = true;
          // this.$refs.vueEditor.quill.removeFormat(0, this.$refs.vueEditor.quill.getLength());
          // setTimeout(() => {
          //   this.$refs.vueEditor.quill.setText(
          //     this.$refs.vueEditor.quill.getText(
          //       0,
          //       this.$refs.vueEditor.quill.getLength(),
          //     ).toString(),
          //   );
          // }, 0);
        }, 0);
      }
    },
    /**
     * Calculates the position of a caret and triggers the event if caret is on the last line.
     */
    checkBottom() {},
    /**
     * Calculates the position of a caret and triggers the event if caret is on the first line.
     */
    checkTop() {},
    /**
     * Notifies the parent that the focus has been lost.
     */
    clear() {
      this.$refs.vueEditor.quill.setText('');
    },
    gotFocus() {
      this.$emit('gotFocus');
    },
    /** There is a wrapper which when clicked focuses this input field. To prevent it from refocusing and losing the selection if user makes one, we need to prevent bubbling since the input has already been clicked and focused. */
    preventPropagation(event) {
      event.stopPropagation();
      if (!this.$refs.vueEditor.quill.hasFocus()) {
        this.$refs.vueEditor.quill.focus();
      }
    },
    italicIt() {
      const isItalicAlready = this.$refs.vueEditor.quill.getFormat(this.selection.index, this.selection.length).italic;
      this.$refs.vueEditor.quill.formatText(this.selection.index, this.selection.length, 'italic', !isItalicAlready);
      this.$refs.vueEditor.quill.focus();
      this.$refs.vueEditor.quill.format('italic', !isItalicAlready);
      this.checkFormatting();
    },
    /**
     * Invoked when the 'enter' key was pressed and submits the message if submitting is not prevented.
     * We prevent submit via props.
     * Preventing submit is useful in media modal so we do not accidentally submit media before populating caption on other media if we need to.
     */
    keypressHandler() {
      if (!this.preventSubmit) {
        this.submitMessage();
      } else {
        // NOTE: QUILL scroll into carret position
        const lastPosition = this.$refs.vueEditor.quill.getSelection() ? { ...this.$refs.vueEditor.quill.getSelection(), contentLength: this.$refs.vueEditor.quill.getText().trim().length } : {};
        const bounds = this.$refs.vueEditor.quill.getBounds(lastPosition.index);
        this.$refs.vueEditor.$el.childNodes[1].scrollTo(0, bounds.bottom);
      }
    },
    lostFocus() {
      this.$emit('lostFocus');
    },
    /**
     * Saves the selection data.
     *
     * @param range Selection object
     */
    onSelectionChange(range) {
      if (!this.pasted) {
        this.lastPosition = this.$refs.vueEditor.quill.getSelection() ? { ...this.$refs.vueEditor.quill.getSelection(), contentLength: this.$refs.vueEditor.quill.getText().trim().length } : {};
      }
      if (this.ignoreSelectionChange) {
        return;
      }

      if (this.$refs.vueEditor.quill.getSelection()) {
        let direction = 'left';
        if (range.index > this.selection.index) {
          direction = 'right';
        }
        const deltasFromCurrentPosition = this.$refs.vueEditor.quill.getContents(this.$refs.vueEditor.quill.getSelection().index - 1);
        
        // If user changes the cursor position to be inside a tag we need to automatically move it outside because tags should not be edited by user from the text input area.
        if (
          // direction === 'right' && // This is just for testing
          (
            deltasFromCurrentPosition.ops &&
            deltasFromCurrentPosition.ops[0] &&
            deltasFromCurrentPosition.ops[0].insert === '\n'
          ) ||
          (
            deltasFromCurrentPosition.ops &&
            deltasFromCurrentPosition.ops[0] &&
            deltasFromCurrentPosition.ops[0].attributes &&
            deltasFromCurrentPosition.ops[0].attributes.code
          )
        ) {
          if (direction === 'right') {
            this.$refs.vueEditor.quill.setSelection(this.$refs.vueEditor.quill.getSelection().index + 1, 0);
          } else {
            let currentPosMinusOne = this.$refs.vueEditor.quill.getSelection().index - 1;
            if (currentPosMinusOne < 0) {
              currentPosMinusOne = 0;
            }
            this.$refs.vueEditor.quill.setSelection(currentPosMinusOne, 0);
            this.pauseLoop = true;
          }
        } else if (range) {
          this.selection = { ...range };
          this.checkFormatting();
        }
      }

      // Check if selection index is zero and first item is tag. If it is move the cursor after the tag because otherwise cursor is in the tag which should not be possible.
      if (range && range.index === 0) {
        const currentContent = this.$refs.vueEditor.quill.getContents(0);
        if (
          currentContent &&
          currentContent.ops[0] &&
          currentContent.ops[0].attributes &&
          currentContent.ops[0].attributes.code
        ) {
          this.$refs.vueEditor.quill.insertText(0, ' ', 'code', false);
          this.$refs.vueEditor.quill.setSelection(1, 0);
          this.setFakeIndent = true;
        } else if ( // Prevent going on a indented space. This space is just for enabling the cursor to position before the tag.
          currentContent &&
          currentContent.ops[0] &&
          currentContent.ops[0].insert === ' ' &&
          currentContent.ops[1] &&
          currentContent.ops[1].attributes &&
          currentContent.ops[1].attributes.code
        ) {
          this.$refs.vueEditor.quill.setSelection(1, 0);
        }
        this.$nextTick(() => {
          this.pauseLoop = false;
        });
      }

      if (this.pauseLoop) {
        return;
      }
      
      if (range && range.index >= 2) {
        const currentContent = this.$refs.vueEditor.quill.getContents();
        if (
          currentContent &&
          currentContent.ops[0] &&
          currentContent.ops[0].insert &&
          typeof currentContent.ops[0].insert === 'string' &&
          currentContent.ops[0].insert.startsWith(' ')
        ) {
          this.$refs.vueEditor.quill.deleteText(0, 1);
          this.$refs.vueEditor.quill.setSelection(range.index - 1, 0);
          this.setFakeIndent = false;
        }
      }
    },
    unpauseChangeListener() {
      clearTimeout(this.unpauseTimeout);
      this.unpauseTimeout = setTimeout(() => {
        this.pauseChangeListener = false;
      }, 100);
    },
    /**
     * Emits an input event to enable v-model on the component.
     */
    onTextChange(changes, oldDeltas) {
      setTimeout(() => {
        this.$emit('raw-input', this.$refs.vueEditor.quill.getText());
        // TODO - FINISH THIS sb input max counter
        // console.log(1, this.maxlength);
        // console.log(2, this.$refs.vueEditor.quill.getText().trim(), this.$refs.vueEditor.quill.getText().trim().length);
        // if (this.skipCycle) {
        //   this.skipCycle = false;
        //   return;
        // }
        // if (this.maxlength && this.$refs.vueEditor.quill.getText().trim().length > this.maxlength) {
        //   this.skipCycle = true;
        //   const currentPosMinusOne = this.$refs.vueEditor.quill.getSelection().index - 1;
        //   console.log('POS', currentPosMinusOne);
        //   this.$refs.vueEditor.quill.setContents(oldDeltas);
        //   this.$nextTick(() => {
        //     this.$refs.vueEditor.quill.setSelection(currentPosMinusOne, 0);
        //   });
          
        //   console.log('return');
        //   return;
        // }

        if (this.pauseChangeListener) {
          return;
        }

        this.pauseChangeListener = true;
        const selection = this.$refs.vueEditor.quill.getSelection();
        // first check if it was a delete and if deleted character is space
        if (
          changes.ops &&
          changes.ops[1] &&
          changes.ops[1].delete
          // this.oldText[changes.ops[0].retain] === ' '
        ) {
          const deletePosition = selection ? selection.index : null;

          this.oldDeltasQuill.setContents(oldDeltas);
          const deltasFromCurrentPosition = this.oldDeltasQuill.getContents(deletePosition - this.deleteDirection);
          const deleteDeltaIndex = oldDeltas.ops.length - deltasFromCurrentPosition.ops.length;

          // check if delta to delete is a tag('code').
          if (!oldDeltas.ops[deleteDeltaIndex] || !oldDeltas.ops[deleteDeltaIndex].attributes || !oldDeltas.ops[deleteDeltaIndex].attributes.code) {
            this.unpauseChangeListener();
          } else {
            const deleteDeltaLength = this.deleteDirection > 0 ? oldDeltas.ops[deleteDeltaIndex].insert.toString().length : 0;

            if (
              oldDeltas.ops[deleteDeltaIndex + 1] &&
              oldDeltas.ops[deleteDeltaIndex + 1].insert &&
              oldDeltas.ops[deleteDeltaIndex + 1].insert === ' '
            ) {
              oldDeltas.ops.splice(deleteDeltaIndex, 2); // removing two elements because we are working with oldDeltas so we also need to delete the space user has already deleted but in this case next delta is only space and not for example ' abc' whish is the text delta and not the code delta after the string
            } else {
              oldDeltas.ops.splice(deleteDeltaIndex, 1);
              try {
                oldDeltas.ops[deleteDeltaIndex].insert = oldDeltas.ops[deleteDeltaIndex].insert.startsWith(' ') ? oldDeltas.ops[deleteDeltaIndex].insert.slice(1) : oldDeltas.ops[deleteDeltaIndex].insert;
              } catch (error) { }
            }

            this.resetTextTo(oldDeltas.ops);
            this.$refs.vueEditor.quill.setSelection(deletePosition - deleteDeltaLength, 0);

            const countTagItems = this.$refs.vueEditor.quill.getContents().ops.filter(item => item.attributes && item.attributes.code === true).length;
            this.$emit('tag-count', countTagItems);
            this.setTagNumbers();
          }

          // console.log('DELETED');
          this.reCountTagNumbers();
        }

        if (this.slim) {
          this.$emit('input', this.newOutboundMessage.replace(/(<\/?)([p]+)(>|$)/gi, '$1span>').replace(/(<\/?[br]+>|$)/gi, ''));
        } else {
          this.$emit('input', this.newOutboundMessage);
        }
        this.onSelectionChange(this.$refs.vueEditor.quill.getSelection());
        // Check if this is initial text and run tag counter on it if it is.
        if (!this.oldText) {
          // this.setTagNumbers(); // This causes issue when first character is inserted
          this.$refs.vueEditor.quill.setSelection(this.$refs.vueEditor.quill.getLength(), 0);
        }
        this.oldText = this.$refs.vueEditor.quill.getText();
        this.unpauseChangeListener();

        const deltaFromCurrent = this.$refs.vueEditor.quill.getSelection() ? this.$refs.vueEditor.quill.getContents(this.$refs.vueEditor.quill.getSelection().index) : null;
        if (
          changes.ops &&
          changes.ops[1] &&
          changes.ops[1].insert === '\n' &&
          deltaFromCurrent &&
          deltaFromCurrent.ops &&
          deltaFromCurrent.ops[0] &&
          deltaFromCurrent.ops[0].insert.endsWith('\n') &&
          deltaFromCurrent.ops[1] &&
          deltaFromCurrent.ops[1].attributes &&
          deltaFromCurrent.ops[1].attributes.code
        ) {
          this.$nextTick(() => {
            this.$refs.vueEditor.quill.setSelection(this.$refs.vueEditor.quill.getSelection().index + 1, 0);
          });
        }
        if (this.pasted) {
          this.pauseChangeListener = true;
          // this.$refs.vueEditor.quill.setText(this.$refs.vueEditor.quill.getText().replace(/(\\n)*$/g, '').trim());
          setTimeout(() => {
            const totalLengthBeforeRemove = this.$refs.vueEditor.quill.getText().trim().length + this.lastPosition.length;
            const diff = totalLengthBeforeRemove - this.lastPosition.contentLength;
            const cursorPosition = this.lastPosition.index + diff;
            this.$refs.vueEditor.quill.setSelection(cursorPosition, 0);
            this.$refs.vueEditor.quill.removeFormat(cursorPosition - diff, diff);
            this.$nextTick(() => {
              this.pasted = false;
            });
            
            // console.log('PASTED');
            this.reCountTagNumbers();
          }, 10);
        }
      }, 100);
    },
    resetTextTo(text) {
      if (typeof text === 'string') {
        this.$refs.vueEditor.quill.setText(whatsAppFormatToHTML(text));
      } else {
        // text has to be quill formated array - https://quilljs.com/docs/api/#setcontents
        text.forEach((delta) => {
          if (delta.attributes && delta.attributes.code) {
            delta.insert = convertUnderscoreToSpace(delta.insert);
          }
        });
        this.$refs.vueEditor.quill.setContents(text);
      }
      // this.$emit('raw-input', this.$refs.vueEditor.quill.getText());
      // if (this.slim) {
      //   this.$emit('input', this.newOutboundMessage.replace(/(<\/?)([p]+)(>|$)/gi, '$1span>').replace(/(<\/?[br]+>|$)/gi, ''));
      // } else {
      //   this.$emit('input', this.newOutboundMessage);
      // }
    },
    setHtml(htmlInput) {
      this.$refs.vueEditor.quill.clipboard.dangerouslyPasteHTML(`<div>${htmlInput}</div>`); // must be wrapped with div because if multiple tags without a parent are set then spaces between tags are removed
      this.focus();
    },
    strikethroughIt() {
      const isStrikeAlready = this.$refs.vueEditor.quill.getFormat(this.selection.index, this.selection.length).strike;
      this.$refs.vueEditor.quill.formatText(this.selection.index, this.selection.length, 'strike', !isStrikeAlready);
      this.$refs.vueEditor.quill.focus();
      this.$refs.vueEditor.quill.format('strike', !isStrikeAlready);
      this.checkFormatting();
    },
    submitMessage() {
      if (this.slim) {
        this.$emit('input', removeExtraSpaces(whatsappFormatMessage(this.newOutboundMessage.replace(/(<\/?)([p]+)(>|$)/gi, '$1span>'))));
      } else {
        this.$emit('submit', removeExtraSpaces(whatsappFormatMessage(this.newOutboundMessage))); // TODO try without removeExtraSpaces method to make sure this works fine in preview and when template is sent.
      }
    },
    focus() {
      const length = this.$refs.vueEditor.quill.getLength();
      this.$refs.vueEditor.quill.setSelection(length, 0);
      this.$refs.vueEditor.quill.focus();
    },
  },
};
</script>

<style lang="scss">
#vue-editor-wrapper {
  min-height: 42px;
  max-height: 150px;
  overflow-y: auto;
}

.slim #vue-editor-wrapper {
  min-height: 30px;
  max-height: 30px;
  overflow-y: hidden;
  padding: 6px 4px 5px 10px !important;
}
</style>

<style lang="scss" scoped>
#quill-worker {
  display: none !important;
}

.fake-indent ::v-deep {
  .ql-container {
    .ql-editor > p:first-child {
      text-indent: -4px;
    }
  }
}

::v-deep .ql-container {
  border: none !important;
  padding: 10px 10px 10px 10px;

  .ql-editor {
    padding: 0;
    // min-height: unset;
    min-height: 35px; // NOTE: Grammarly is not being initialized if the initial height of the element is smaller.
    font-size: 14px;
    line-height: 1.2;
    color: $blackPrimary;
    overflow-wrap: anywhere;
    word-break: break-word;
    // This fixes issue with different font line heights which on some systems can show native scrollbar next to the custom one we already have.
    // Also braks the height of the input area.
    // overflow: visible;

    &.ql-blank::before {
      // padding: 10px 10px 0 10px;
      color: $greyPrimary;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.2;
      left: 10px;
      right: 10px;
    }

    strong, strong em, strong s {
      font-weight: 600 !important;
    }

    code {
      font-family: HelveticaNeue !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      color: $blackPrimary !important;
      line-height: 1.42;
      padding: 0 7px !important;
      border-radius: 9px !important;
      border: solid 1px $blackPrimary !important;
      background-color: $white !important;
      margin-right: -3px;

      &:has(strong) {
        border-color: $redPrimary !important;
      }

      strong {
        font-size: 12px !important;
        font-weight: 400 !important;
        color: $redPrimary;
      }
    }

    &[contenteditable="false"] * {
      cursor: default;
    }
  }
}

.slim ::v-deep {
  max-width: 100%;
  overflow-y: hidden;

  .ql-container {
    grammarly-extension {
      display: none;
    }

    .ql-editor {
      white-space: pre;

      & > p {
        display: inline;
        width: fit-content;
      }

      br {
        display: none;
      }
    }
  }
}
</style>
